const DataStoreNames = require('@/lib/DataStores/DataStoreNames');
const { normalizeBarcode } = require('@/lib/Barcodes');

class ItemBarcodeRepository {
    /**
     * @param {DataStore} dataStore
     */
    constructor(dataStore) {
        this.dataStore = dataStore;
    }

    /**
     * @private
     *
     * @return {Dexie.Table}
     */
    getStore() {
        return this.dataStore.getStore(DataStoreNames.ITEM_BARCODES);
    }

    /**
     * @param {string} barcode
     *
     * @return {Promise<ItemBarcodeRow[]>}
     */
    findByBarcode(barcode) {
        return this.getStore()
            .where({ barcode: normalizeBarcode(barcode) })
            .toArray();
    }

    /**
     * Replaces all barcodes stored for one item.
     *
     * @param {AbstractItem} item
     *
     * @return {Promise}
     */
    async replaceForItem(item) {
        // Delete all barcodes for the given item first so we do not leave any old data.
        await this.getStore()
            .where('[itemID+barcode]')
            .between(
                // The primary key begins with itemID so we can use that index instead of needing another.
                [item.ID, this.dataStore.dexieMinKey()],
                [item.ID, this.dataStore.dexieMaxKey()]
            )
            .delete();

        /**
         * @type {ItemBarcodeRow[]}
         */
        const rows = item.barcodes.map((barcode) => ({
            itemID: item.ID,
            itemType: item.itemType,
            barcode: normalizeBarcode(barcode),
        }));

        await this.getStore().bulkPut(rows);
    }

    /**
     * Replaces everything stored for each of the given items.
     * The syncer passes a list of items here, so this runs in one transaction rather than leaving Dexie
     * to open one per operation.
     *
     * @param {AbstractItem[]} items
     *
     * @return {Promise}
     */
    async replaceForItems(items) {
        return this.dataStore.transaction(
            this.dataStore.TRANSACTION_MODE.READWRITE,
            [
                this.getStore(),
            ],
            async () => {
                for (const item of items) {
                    await this.replaceForItem(item);
                }
            }
        );
    }
}

module.exports = ItemBarcodeRepository;
